import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiExternalLink } = FiIcons;

const PLACE_ID = 'ChIJ338D-Wb84TgRgeat_066vRE';
const GOOGLE_MAPS_URL = `https://www.google.com/maps/place/?q=place_id:${PLACE_ID}`;
const REVIEW_URL = `https://search.google.com/local/writereview?placeid=${PLACE_ID}`;

const FALLBACK_REVIEWS = [
  { author_name: 'Mohammad Ashraf', rating: 5, text: 'Tayyab Electric Studio did a complete wiring job for my new house. The work was clean, professional and finished on time. Highly recommended!', relative_time_description: '2 months ago', profile_photo_url: null },
  { author_name: 'Riyaz Ahmed', rating: 5, text: 'Got CCTV cameras installed for my shop. The installation was done perfectly, remote access works great on my phone. Top service!', relative_time_description: '3 months ago', profile_photo_url: null },
  { author_name: 'Farooq Mir', rating: 5, text: 'We signed up for the Premium Care Plan and it has been worth every rupee. Monthly inspections, quick response whenever something goes wrong.', relative_time_description: '1 month ago', profile_photo_url: null },
  { author_name: 'Gulzar Khan', rating: 5, text: 'Called them for emergency repair at night — they responded within the hour. This kind of 24/7 service is rare. Will always call Tayyab Electric first!', relative_time_description: '4 months ago', profile_photo_url: null },
];

const StarRating = ({ rating }) => (
  <div className="flex space-x-0.5">
    {[...Array(5)].map((_, i) => (
      <SafeIcon key={i} icon={FiStar} className={`text-sm ${i < rating ? 'text-yellow-400' : 'text-gray-600'}`}
        style={i < rating ? { fill: '#facc15' } : {}} />
    ))}
  </div>
);

const Avatar = ({ name, photo }) => {
  if (photo) return <img src={photo} alt={name} className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />;
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['from-blue-500 to-cyan-500', 'from-purple-500 to-pink-500', 'from-green-500 to-teal-500', 'from-yellow-500 to-orange-500'];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
      <span className="text-white font-bold text-sm">{initials}</span>
    </div>
  );
};

const GoogleReviews = () => {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);
  const [rating, setRating] = useState(5.0);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(0);
  const [isReal, setIsReal] = useState(false);

  // ✅ Fetch real reviews from Netlify function
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await fetch('/api/reviews');
        const data = await res.json();
        if (data.success && data.reviews?.length > 0) {
          setReviews(data.reviews);
          setRating(data.rating || 5.0);
          setTotal(data.total || 0);
          setIsReal(true);
        }
      } catch {
        // silently use fallback reviews
      }
    };
    fetchReviews();
  }, []);

  const visible = [
    reviews[(current - 1 + reviews.length) % reviews.length],
    reviews[current],
    reviews[(current + 1) % reviews.length],
  ];

  useEffect(() => {
    const id = setInterval(() => setCurrent(i => (i + 1) % reviews.length), 4000);
    return () => clearInterval(id);
  }, [reviews.length]);

  return (
    <section className="py-10 bg-gray-900/20 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
      <div className="relative max-w-6xl mx-auto px-4">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 mb-3">
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
            <span className="text-gray-400 text-sm font-medium">
              Google Reviews {isReal && <span className="text-green-400 text-xs ml-1">● Live</span>}
            </span>
          </div>
          <div className="flex items-center justify-center space-x-3 mb-2">
            <span className="text-5xl font-black text-white">{rating.toFixed(1)}</span>
            <div>
              <div className="flex space-x-1 mb-1">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-xl" style={{ fill: '#facc15' }} />
                ))}
              </div>
              <p className="text-gray-500 text-xs">
                Based on {total > 0 ? `${total}` : reviews.length + '+'} reviews
              </p>
            </div>
          </div>
          <a href={REVIEW_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-sm transition-colors">
            <span>Write a Review</span><SafeIcon icon={FiExternalLink} className="text-xs" />
          </a>
        </div>

        {/* Review cards */}
        <div className="flex items-center justify-center space-x-4 overflow-hidden">
          {visible.map((r, i) => (
            <motion.div key={`${r.author_name}-${i}`}
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}
              className={`bg-gray-900 border rounded-2xl p-5 flex-shrink-0 transition-all duration-500 ${i === 1 ? 'border-blue-500/40 shadow-lg shadow-blue-500/10 w-72 md:w-80' : 'border-gray-800 opacity-50 w-60 hidden md:block'}`}
              style={{ transform: i === 1 ? 'scale(1.03)' : 'scale(0.97)' }}>
              <div className="flex items-center space-x-3 mb-3">
                <Avatar name={r.author_name} photo={r.profile_photo_url} />
                <div>
                  <p className="text-white font-semibold text-sm">{r.author_name}</p>
                  <p className="text-gray-500 text-xs">{r.relative_time_description}</p>
                </div>
                <img src="https://www.google.com/favicon.ico" alt="G" className="w-4 h-4 ml-auto opacity-50" />
              </div>
              <StarRating rating={r.rating} />
              <p className="text-gray-300 text-sm leading-relaxed mt-3 line-clamp-4">"{r.text}"</p>
            </motion.div>
          ))}
        </div>

        {/* Dots */}
        <div className="flex justify-center space-x-2 mt-6">
          {reviews.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`rounded-full transition-all duration-300 ${i === current ? 'w-5 h-2 bg-blue-400' : 'w-2 h-2 bg-gray-700'}`} />
          ))}
        </div>

        {/* View all */}
        <div className="text-center mt-5">
          <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 border border-gray-700 text-gray-300 hover:border-blue-500 hover:text-white px-5 py-2.5 rounded-2xl text-sm transition-all">
            <img src="https://www.google.com/favicon.ico" alt="G" className="w-4 h-4" />
            <span>View all reviews on Google</span>
            <SafeIcon icon={FiExternalLink} className="text-xs" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;